import { Constants } from "@eagerdog/constants";

// Values live in Constants (docs/dev-standards/constants-and-enums.md); the
// types derive from them so the two cannot drift.
export type PostAuthorType = (typeof Constants.post_author_type)[keyof typeof Constants.post_author_type];
export type PostType = (typeof Constants.post_type)[keyof typeof Constants.post_type];
export type ShareChannel = (typeof Constants.share_channel)[keyof typeof Constants.share_channel];
export type PostMediaType = (typeof Constants.post_media_type)[keyof typeof Constants.post_media_type];

/** System-generated activity posts: no user-entered content, never screened. */
export const ACTIVITY_POST_TYPES: PostType[] = [
  Constants.post_type.event_published,
  Constants.post_type.event_registered,
  Constants.post_type.course_enrolled,
  Constants.post_type.club_followed,
  Constants.post_type.dog_followed,
  Constants.post_type.result_entered,
];

/**
 * Display snapshot for an activity post, captured when the activity happens
 * so the card renders without joining to events / courses / clubs / dogs.
 * Only the fields relevant to the post type are set.
 */
/** A dog named on an activity post (e.g. the dogs entered in an event). */
export interface IPostActivityDog {
  dog_id: string;
  call_name?: string;
  image_url?: string;
}

export interface IPostActivity {
  // event_published / event_registered
  event_id?: string;
  event_handle?: string;
  event_title?: string;
  sanctioning_club?: string;
  // event_registered: every dog this user has entered in the event, in entry
  // order. The post is per (user, event); later entries are merged in.
  dogs?: IPostActivityDog[];
  // course_enrolled
  course_id?: string;
  course_handle?: string;
  course_title?: string;
  // shared by event / course
  club_id?: string;
  club_name?: string;
  start_date?: Date;
  end_date?: Date;
  city?: string;
  state?: string;
  // club_followed / dog_followed
  target_id?: string;
  target_name?: string;
  target_image_url?: string;
  target_breed?: string;
}

/** Length of the short share code on posts (`IPost.handle`). */
export const POST_HANDLE_LENGTH = 8;

export interface IPostMedia {
  type: PostMediaType;
  url: string;
  thumbnail_url?: string;
}

/**
 * Display snapshot of the run behind a run_result post, captured server-side
 * when the post is created so the feed card can show what was actually
 * logged without joining to dog_runs. Only fields the card renders.
 */
export interface IPostRunSummary {
  show_type: string;
  sanctioning_club: string;
  trial_date?: Date;
  event_name?: string;
  city?: string;
  state?: string;

  class?: string;
  level?: string;
  element?: string;
  section?: string;
  division?: string;

  result_status?: string;
  qualified?: boolean;
  score?: number;
  placement?: number;
  time?: string;
  time_seconds?: number;
  mph?: number;
  points_earned?: number;
  faults?: number;
  hides_found?: number;
  awards?: string[];

  // result_entered: the club-entered score behind the post.
  score_id?: string;
  show_name?: string;
  event_handle?: string;
  judge_name?: string;
  /** Display label from scoring-core (e.g. "Qualified", "NQ", "Pass"). */
  result_label?: string;
  /** Show-type-specific stats, exactly as the Results grid shows them. */
  fields?: IPostResultField[];
}

export interface IPostResultField {
  label: string;
  value: string;
}

export type PostModerationStatus =
  (typeof Constants.post_moderation_status)[keyof typeof Constants.post_moderation_status];

export type PostReportReason =
  (typeof Constants.post_report_reason)[keyof typeof Constants.post_report_reason];

export type PostScreeningProvider = "rekognition" | "openai";
export type PostScreeningStatus = "clean" | "flagged" | "blocked" | "skipped" | "unsupported";

export interface IPostScreeningLabel {
  name: string;
  parent?: string;
  confidence: number; // 0–100
}

/** One screening pass over a post (one per provider). Kept in full so thresholds can be re-evaluated later without re-calling the API. */
export interface IPostScreeningResult {
  provider: PostScreeningProvider;
  status: PostScreeningStatus;
  labels?: IPostScreeningLabel[];
  checked_at: Date;
  error?: string;
}

export interface IPostModeration {
  report_count: number;          // distinct reporters, all accounts
  counted_report_count: number;  // reporters past the minimum account age
  auto_hidden_at?: Date;
  screening?: IPostScreeningResult[];
  removed_at?: Date;
  removed_by?: string;
  removed_reason?: PostReportReason;
  resolved_at?: Date;
}

export interface IPost {
  _id: string;
  /** Short share code used in permalinks (/p/:handle). Set on save; older posts are backfilled. */
  handle?: string;

  author_type: PostAuthorType;
  author_id: string;
  post_type: PostType;

  // Social feed moderation. Missing on documents created before the
  // feature; treated as "visible".
  moderation_status?: PostModerationStatus;
  moderation?: IPostModeration;

  body?: string;
  media?: IPostMedia[];

  dog_id?: string;
  /**
   * Posts about several dogs at once (event_registered). Scopes the post into
   * each dog's profile feed and the feeds of people following those dogs,
   * the same way `dog_id` does for a single dog.
   */
  dog_ids?: string[];
  run_result_id?: string;
  run_summary?: IPostRunSummary;
  linked_event_id?: string;
  /** result_entered: the `scores` document this post reflects. */
  score_id?: string;

  // Snapshot for system-generated activity posts.
  activity?: IPostActivity;

  like_count: number;
  share_count: number;

  created_at: Date;
  updated_at: Date;
  /** Set when the author last changed the text; absent on never-edited posts. */
  edited_at?: Date;

  // Denormalized display snapshot, captured at creation time so the feed
  // doesn't need to join out to users/clubs/dogs on every read.
  author_display_name?: string;
  author_image_url?: string;
  dog_call_name?: string;
  dog_image_url?: string;

  // Snapshot for a title_earned post, captured when the title is earned.
  title_code?: string;
  title_name?: string;
  sanctioning_club?: string;
  show_type?: string;
  dog_breed?: string;
  points?: number;
  fastest_mph?: number;
  average_mph?: number;
  total_runs?: number;

  // Populated per-viewer at read time (not stored)
  liked_by_viewer?: boolean;
  /** Viewer is the author, or manages the club that authored it. */
  viewer_can_delete?: boolean;
  /** viewer_can_delete, and the post type carries editable text (EDITABLE_POST_TYPES). */
  viewer_can_edit?: boolean;
}
